import { Collapse } from 'antd';
import { useField } from 'formik';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useListInsuranceProductsQuery } from '../../../../api/queries';
import TransferField from '../../../../components/fields/TransferField';
import CollapsibleWrapper from '../../../../components/wrappers/CollapsibleWrapper';

const { Panel } = Collapse;

export type InsuranceProductAssignmentSectionProps = {
    moduleId: string;
    variantSuiteIds?: string[] | null;
    disabled?: boolean;
};

type RecordType = {
    key: string;
    title: string;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const InsuranceProductAssignmentSection = ({
    moduleId,
    variantSuiteIds = null,
    disabled,
}: InsuranceProductAssignmentSectionProps) => {
    const { t } = useTranslation(['dealershipManagement']);

    const [{ value: currentValue }, , { setValue }] = useField<string[]>('dealerInsuranceProductSuiteIds');

    const { data: localInsuranceProductData } = useListInsuranceProductsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { moduleId } },
    });

    const insuranceProductDataSource: RecordType[] = useMemo(() => {
        if (!localInsuranceProductData) {
            return [];
        }

        const products = localInsuranceProductData.list.items;

        if (variantSuiteIds) {
            return products
                .filter(product => product.allVariantSuiteIds.some(id => variantSuiteIds.includes(id)))
                .map(product => ({
                    key: product.versioning.suiteId,
                    title: product.displayName,
                }));
        }

        return products.map(product => ({
            key: product.versioning.suiteId,
            title: product.displayName,
        }));
    }, [localInsuranceProductData, variantSuiteIds]);

    useLayoutEffect(() => {
        if (variantSuiteIds && localInsuranceProductData) {
            const validSuiteIds = new Set(insuranceProductDataSource.map(data => data.key));
            const nextValue = currentValue.filter(element => validSuiteIds.has(element));

            if (nextValue.length !== currentValue.length) {
                setValue(nextValue);
            }
        }
    }, [variantSuiteIds, localInsuranceProductData, insuranceProductDataSource, currentValue, setValue]);

    return (
        <CollapsibleWrapper defaultActiveKey={['assignInsuranceProduct']}>
            <Panel
                key="assignInsuranceProduct"
                header={t('dealershipManagement:tabs.panel.insuranceProductAssignment')}
            >
                <TransferField
                    dataSource={insuranceProductDataSource}
                    disabled={disabled}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name="dealerInsuranceProductSuiteIds"
                    render={item => item.title}
                />
            </Panel>
        </CollapsibleWrapper>
    );
};

export default InsuranceProductAssignmentSection;
